import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '../styles/video-overlay.css';

interface VideoOverlayProps {
  onComplete: () => void;
  skipVideo?: boolean;
}

const VideoOverlay: React.FC<VideoOverlayProps> = ({ onComplete, skipVideo = false }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [canSkip, setCanSkip] = useState(true); // Allow immediate skip by clicking
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();

  // Define handleSkip before useEffect
  const handleSkip = useCallback(() => {
    console.log('Skip clicked', { canSkip, isTransitioning });
    if (!isTransitioning) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setIsTransitioning(true);
      
      // Start slow-to-fast transition
      setTimeout(() => {
        // Unlock body scroll before transitioning
        document.body.classList.remove('video-overlay-active');
        setIsVisible(false);
        onComplete();
      }, 100);
    }
  }, [isTransitioning, onComplete]);

  useEffect(() => {
    // Lock body scroll when video overlay is active
    document.body.classList.add('video-overlay-active');

    if (skipVideo) {
      handleSkip();
      return;
    }

    // Keyboard event listener for skipping
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        handleSkip();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      // Unlock body scroll when component unmounts
      document.body.classList.remove('video-overlay-active');
    };
  }, [skipVideo, handleSkip]);

  const handleVideoEnd = () => {
    setIsTransitioning(true);
    
    // Start slow-to-fast transition
    setTimeout(() => {
      // Unlock body scroll before transitioning
      document.body.classList.remove('video-overlay-active');
      setIsVisible(false);
      onComplete();
    }, 100);
  };


  const handleVideoError = () => {
    console.warn('Video failed to load, skipping to login');
    setVideoLoaded(false);
    // Give a brief moment to show the loading screen, then skip
    setTimeout(() => {
      handleVideoEnd();
    }, 1000);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.5,
            transition: {
              duration: 1.2,
              ease: [0.25, 0.1, 0.25, 1.0], // Slow start, fast end
            }
          }}
          className="video-overlay-container fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden touch-none select-none"
        >
          {/* Video Element */}
          <video
            ref={videoRef}
            className="video-overlay-video absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            playsInline
            onEnded={handleVideoEnd}
            onError={handleVideoError}
            onLoadedData={() => {
              console.log('Video loaded successfully');
              setVideoLoaded(true);
            }}
            onCanPlay={() => console.log('Video ready to play')}
            preload="auto"
            style={{ objectPosition: 'center' }}
          >
            <source src="/intro-video.mp4" type="video/mp4" />
            <p className="text-white text-center">Your browser does not support video playback.</p>
          </video>

          {/* Gradient Overlay for Better Text Visibility */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: canSkip ? 0.3 : 0 }}
            className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30 pointer-events-none"
          />

          {/* Skip Button */}
          <AnimatePresence>
            {canSkip && !isTransitioning && (
              <motion.button
                key="skip-button"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: 0.2 }} // Reduced delay for immediate visibility
                onClick={handleSkip}
                className="absolute bottom-8 right-8 z-10 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium hover:bg-white/20 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2">
                  <span>Skip Intro</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </div>
              </motion.button>
            )}
            
            {/* Keyboard hint */}
            {canSkip && !isTransitioning && (
              <motion.div
                key="keyboard-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.5 }} // Show hint quickly
                className="absolute bottom-16 right-8 text-xs text-white/60"
              >
                Click anywhere or press ESC, Space, Enter to skip
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading Indicator (shows while video is loading) */}
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: videoLoaded ? 0 : 1 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex items-center justify-center bg-black z-10"
          >
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="inline-block w-12 h-12 border-4 border-white/20 border-t-white rounded-full mb-4"
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-white text-lg font-medium tracking-wider"
              >
                LOOPLET AI
              </motion.p>
            </div>
          </motion.div>

          {/* Click anywhere to skip (immediate) */}
          {canSkip && !isTransitioning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0 }} // No delay, immediately clickable
              onClick={handleSkip}
              className="absolute inset-0 cursor-pointer"
              title="Click to skip intro"
            />
          )}

          {/* Transition Overlay */}
          <AnimatePresence>
            {isTransitioning && (
              <motion.div
                key="transition-overlay"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: 1, 
                  scale: 2,
                  transition: {
                    duration: 1.2,
                    ease: [0.25, 0.1, 0.25, 1.0],
                  }
                }}
                className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-cyan-900 opacity-80"
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoOverlay;